import { calculatePropertyCompliance, isDefaultComplianceOptions } from '@strenforcement/domain/dashboardCompliance';
import { CURRENT_DASHBOARD_SNAPSHOT_VERSION, dashboardSnapshotDate } from '@strenforcement/domain/dashboardSnapshot';

const dayMs = 86400000;
const validRate = value => Number.isFinite(value) && value >= 0 && value <= 1;
const validCount = value => Number.isSafeInteger(value) && value >= 0;
const dateTime = date => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
  const time = Date.parse(`${date}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === date ? time : null;
};

export const snapshotDate = dashboardSnapshotDate;
export const isRateMetric = metric => metric === 'complianceRate' || metric === 'verificationRate';

export function historyComplianceRate(row, options) {
  const calculation = calculatePropertyCompliance(row.complianceBasis, options);
  // A saved rate is only meaningful for the default policy. Invalid supplied
  // counts must not silently fall back to a rate calculated another way.
  const rate = calculation ? calculation.rate : !row.complianceBasis && isDefaultComplianceOptions(options) ? row.complianceRate : null;
  return validRate(rate) ? rate : null;
}

export function buildDashboardTrend({ rows, metric, value, generatedAt, snapshotVersion, days = 30 }) {
  const endDate = snapshotDate(generatedAt);
  if (!endDate) return { points: [], change: null, comparisonDate: null, comparisonDays: null, startDate: null, endDate: null };
  const end = dateTime(endDate);
  const start = end - days * dayMs;
  const valid = isRateMetric(metric) ? validRate : validCount;
  const comparable = snapshotVersion === CURRENT_DASHBOARD_SNAPSHOT_VERSION;
  const saved = new Map();
  for (const row of rows) {
    const time = dateTime(row.date);
    if (time == null || time < start || time >= end || Date.parse(row.generatedAt) > Date.parse(generatedAt)) continue;
    saved.set(row.date, comparable && row.snapshotVersion === CURRENT_DASHBOARD_SNAPSHOT_VERSION && valid(row[metric]) ? row[metric] : null);
  }
  // The headline's snapshot is authoritative, even if today's archive was
  // rebuilt after it. Never splice a newer saved value onto an older headline.
  saved.set(endDate, comparable && valid(value) ? value : null);
  const points = Array.from({ length: days + 1 }, (_, index) => {
    const date = new Date(start + index * dayMs).toISOString().slice(0, 10);
    return { date, value: saved.get(date) ?? null };
  });
  const last = points.at(-1).value;
  // Use as much comparable history as the selected window contains. The
  // comparison date is explicit so a shorter history never claims a full period.
  const baseline = last != null ? points.find(point => point.date !== endDate && point.value != null) : null;
  return { points, startDate: points[0].date, endDate,
    change: baseline ? last - baseline.value : null,
    comparisonDate: baseline?.date ?? null,
    comparisonDays: baseline ? (end - dateTime(baseline.date)) / dayMs : null };
}
